import { NumberField as BaseNumberField } from "@base-ui/react/number-field";
import { ChevronDown, ChevronUp } from "lucide-react";
import type { CSSProperties, FocusEventHandler, FunctionComponent, Ref } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "./cn";
import { controlBox, useControlDefaults } from "./control";

export interface NumberFieldProps {
  /** `undefined` and `null` both render an empty field; passing the prop at all makes it controlled. */
  value?: number | null;
  defaultValue?: number;
  /** Receives the parsed number, or `null` when the field is cleared (antd `InputNumber` semantics). */
  onChange?: (value: number | null) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  /** Forwarded to the `<input>` so RHF's `field.ref` can focus it. */
  ref?: Ref<HTMLInputElement>;
  name?: string;
  id?: string;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  size?: "small" | "middle" | "large";
  status?: "error" | "warning";
  /** Show the increment/decrement buttons on hover (default `true`). */
  controls?: boolean;
  /** Accepted for antd parity; arrow keys always step. */
  keyboard?: boolean;
  format?: Intl.NumberFormatOptions;
  locale?: Intl.LocalesArgument;
  className?: string;
  style?: CSSProperties;
  "aria-label"?: string;
}

const sizeClass = {
  small: "h-6 text-sm",
  middle: "h-8 text-sm",
  large: "h-10 text-base",
} as const;

const inputPaddingClass = {
  small: "px-[7px]",
  middle: "px-[11px]",
  large: "px-[11px]",
} as const;

const stepButtonClass = cn(
  "flex flex-1 cursor-pointer items-center justify-center text-noa-muted transition-colors",
  "hover:bg-noa-hover hover:text-noa-primary data-[disabled]:cursor-not-allowed data-[disabled]:hover:text-noa-muted"
);

/** Numeric input (antd `InputNumber`), built on Base UI `NumberField`. */
export const NumberField: FunctionComponent<NumberFieldProps> = (allProps) => {
  const {
    value,
    onChange,
    onBlur,
    ref,
    placeholder,
    size: sizeProp,
    status: statusProp,
    id: idProp,
    controls = true,
    keyboard: _keyboard,
    format = { useGrouping: false },
    locale: localeProp,
    disabled,
    className,
    style,
    "aria-label": ariaLabel,
    ...props
  } = allProps;
  // antd treats a present-but-undefined `value` (e.g. an empty RHF field) as controlled-empty.
  const controlled = "value" in allProps;
  // Default to the app language so French users type and see decimal commas.
  const { i18n } = useTranslation();
  const locale = localeProp ?? i18n.language ?? "en-US";
  const { size, status, id, aria } = useControlDefaults({ size: sizeProp, status: statusProp, id: idProp });

  return (
    <BaseNumberField.Root
      {...(controlled ? { value: value ?? null } : {})}
      onValueChange={(next) => onChange?.(next)}
      inputRef={ref}
      format={format}
      locale={locale}
      className={cn("inline-block w-[90px] align-top", className)}
      style={style}
      disabled={disabled}
      {...props}
    >
      <BaseNumberField.Group
        className={cn("group relative flex w-full overflow-hidden", controlBox(status, disabled), sizeClass[size])}
      >
        <BaseNumberField.Input
          id={id}
          {...aria}
          placeholder={placeholder}
          aria-label={ariaLabel}
          onBlur={onBlur}
          className={cn(
            "min-w-0 flex-1 bg-transparent outline-none placeholder:text-noa-disabled disabled:cursor-not-allowed",
            inputPaddingClass[size]
          )}
        />
        {controls && (
          <div className="flex w-[22px] flex-col border-l border-noa opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 group-data-[disabled]:hidden">
            <BaseNumberField.Increment className={stepButtonClass}>
              <ChevronUp className="size-3" />
            </BaseNumberField.Increment>
            <BaseNumberField.Decrement className={cn(stepButtonClass, "border-t border-noa")}>
              <ChevronDown className="size-3" />
            </BaseNumberField.Decrement>
          </div>
        )}
      </BaseNumberField.Group>
    </BaseNumberField.Root>
  );
};
